/**
 * RecallBanner — Phase 6 deliverable.
 *
 * Renders ABOVE the Customer Reviews / video / price sections when this FIN
 * has one or more active CPSC recalls. Reads from the fin_recalls Supabase
 * table (populated by the commerce-cpsc-daily-sweep cron) — per Rule 4, no
 * live CPSC.gov calls at PDP render time.
 *
 * Behavior:
 *   - Renders ONLY if recalls.length > 0 (per Rule 3 — real or hidden, never
 *     fake). DOM element absent when no data.
 *   - Visual weight: high — red border + warning icon. Trust signal.
 *   - CTA de-emphasis (FRIDGE-760, 09/04/2026 — this sentence had promised it
 *     for months while nothing did it): the product page derives
 *     hasActiveRecall from the same rows (lib/product/recall-cta.ts) and
 *     renders every retailer button through RetailerOfferCta, which drops it
 *     to the secondary tone — still a working purchase path — and leads with
 *     a primary "Learn about this recall" link that scrolls to this banner's
 *     id (RECALL_BANNER_ID = "recall-banner"). The banner itself only carries
 *     the anchor; the guard test renders both states.
 *   - Surfaces CPSC URL, hazard description, remedy, units affected, and
 *     manufacturer response.
 *   - Multiple recalls render as separate cards (rare, but supported).
 *
 * Schema.org: parent PDP also emits PublicAlert JSON-LD when this banner is
 * present (Phase 8 / Phase 10 schema extension).
 */

import Link from 'next/link';
import { RECALL_BANNER_ID } from '@/lib/product/recall-cta';

export interface FinRecall {
  id: number;
  fin: string;
  cpsc_recall_id: number;
  cpsc_recall_number: string;
  recall_date: string | null;
  hazard: string | null;
  remedy: string | null;
  injuries_reported: number | null;
  units_affected_count: number | null;
  units_affected_text: string | null;
  manufacturer_response: string | null;
  manufacturers: string[] | null;
  retailers: string[] | null;
  status: 'active' | 'closed' | 'superseded';
  cpsc_url: string;
  pdf_url: string | null;
}

interface RecallBannerProps {
  recalls: FinRecall[] | null | undefined;
  className?: string;
}

export function formatRecallDate(iso: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
  } catch {
    return iso;
  }
}

function formatUnits(count: number | null, text: string | null): string {
  if (count && count > 0) return new Intl.NumberFormat('en-US').format(count) + ' units';
  if (text) return text;
  return '';
}

export function RecallBanner({ recalls, className = '' }: RecallBannerProps) {
  if (!recalls || recalls.length === 0) return null;
  const active = recalls.filter((r) => r.status === 'active');
  if (active.length === 0) return null;

  return (
    <section
      id={RECALL_BANNER_ID}
      className={`rounded-lg border-2 border-red-600 bg-red-50 dark:bg-red-950/30 p-4 sm:p-5 my-4 scroll-mt-28 ${className}`}
      role="alert"
      aria-live="polite"
      data-testid="recall-banner"
      data-recall-count={active.length}
    >
      <div className="flex items-start gap-3">
        <svg
          aria-hidden="true"
          className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div className="flex-1 min-w-0">
          <h2 className="text-base sm:text-lg font-bold text-red-900 dark:text-red-100">
            {active.length === 1 ? 'Active Safety Recall' : `${active.length} Active Safety Recalls`}
          </h2>
          <p className="text-sm text-red-800 dark:text-red-200 mt-0.5">
            This product has been recalled by the U.S. Consumer Product Safety Commission (CPSC).
            Please review the safety information before use or purchase.
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        {active.map((recall) => {
          const dateLabel = formatRecallDate(recall.recall_date);
          const unitsLabel = formatUnits(recall.units_affected_count, recall.units_affected_text);
          return (
            <article
              key={recall.id}
              className="rounded-md bg-white dark:bg-gray-900 border border-red-300 dark:border-red-800 p-3 sm:p-4"
              data-cpsc-recall-number={recall.cpsc_recall_number}
            >
              <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
                <span className="font-mono text-xs uppercase tracking-wider px-1.5 py-0.5 bg-red-600 text-white rounded">
                  CPSC Recall {recall.cpsc_recall_number}
                </span>
                {dateLabel && (
                  <span className="text-xs text-gray-600 dark:text-gray-600">
                    Issued {dateLabel}
                  </span>
                )}
                {unitsLabel && (
                  <span className="text-xs text-gray-600 dark:text-gray-600">
                    Affects {unitsLabel}
                  </span>
                )}
                {recall.injuries_reported != null && recall.injuries_reported > 0 && (
                  <span className="text-xs font-semibold text-red-700 dark:text-red-300">
                    {recall.injuries_reported} injuries reported
                  </span>
                )}
              </header>

              {recall.hazard && (
                <div className="mt-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                    Hazard
                  </h3>
                  <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">{recall.hazard}</p>
                </div>
              )}

              {recall.remedy && (
                <div className="mt-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                    Remedy
                  </h3>
                  <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">{recall.remedy}</p>
                </div>
              )}

              {recall.manufacturers && recall.manufacturers.length > 0 && (
                <div className="mt-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                    Manufacturer{recall.manufacturers.length > 1 ? 's' : ''}
                  </h3>
                  <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                    {recall.manufacturers.join(', ')}
                  </p>
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  href={recall.cpsc_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-md transition-colors"
                >
                  Read full CPSC notice
                  <svg aria-hidden="true" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </Link>
                {recall.pdf_url && (
                  <Link
                    href={recall.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-red-700 dark:text-red-300 border border-red-300 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30 px-3 py-1.5 rounded-md transition-colors"
                  >
                    Official PDF
                  </Link>
                )}
              </div>
            </article>
          );
        })}
      </div>

      <p className="mt-4 text-xs text-gray-700 dark:text-gray-600">
        Recall data sourced from{' '}
        <Link href="https://www.cpsc.gov" target="_blank" rel="noopener noreferrer" className="underline hover:text-red-700 dark:hover:text-red-300">
          CPSC.gov
        </Link>
        . Updated daily by the Fridge.com safety-monitoring system.
      </p>
    </section>
  );
}

export default RecallBanner;
